/* eslint-disable react/display-name -- the per-column cell bodies are render callbacks for PrimeReact's Column, not components (as in table/dataTable.js) */
//React
import React, { useEffect, useId, useMemo, useRef, useState } from "react";
import PropTypes from "prop-types";
import DataTable, { Column, ColumnGroup, Row } from "../table/dataTable";
import IconButton from "../inputs/iconButton";
import HoverTip, { HoverTipScope, hoverTipAnchor } from "../inputs/hoverTip";
import Basic from "../inputs/basic";
import { formatValue, labelEntry, NUMERIC_TYPES } from "./formatters";
import { UrlCell, TextCell, EnumCell, RatioCell, NullCell } from "./cells";
//Assets
import chevronIcon from "../../assets/svgs/chevron-down.svg";
//CSS
import "./charts.scss";

export const PAGE_SIZE = 25;
// Past this many columns (the Open control counts) the table takes the
// DataTable's compact padding and the small type, so an eleven-column manifest
// (By silo, ASB-4409) and the nine-column By domain still fit a card at the
// 1440px evidence width instead of scrolling their last columns out of view.
export const COMPACT_FROM = 8;
// How long the search box waits after the last keystroke before asking the API.
export const SEARCH_DEBOUNCE_MS = 300;
const PAGINATOR = "FirstPageLink PrevPageLink CurrentPageReport NextPageLink LastPageLink";
const PAGE_REPORT = "{first}–{last} of {totalRecords}";

const isBlank = (value) => value === null || value === undefined || value === "";

/** A header's text with a trailing "%" kept on its word when a compact header wraps. */
const headerText = (label) => String(label ?? "").replace(/ %$/, " %");

/**
 * A column's header: its label, underlined when the column has `help` (A.6).
 * The sortable `th` PrimeReact renders around it is the focusable element
 * and the one whose click sorts, so the `th` is the tooltip's anchor too
 * (`hoverTipAnchor` through the Column's pass-through, see `helpAnchor`):
 * hovering or tabbing to the header shows the help, and nothing inside the
 * header takes a tab stop of its own.
 */
function ColumnHeader({ column }) {
	const text = headerText(column.label);
	return (
		<span className="as-col-header">
			{column.help ? (
				<HoverTip
					content={column.help}
					trigger="ancestor"
				>
					{text}
				</HoverTip>
			) : (
				text
			)}
		</span>
	);
}

ColumnHeader.propTypes = {
	column: PropTypes.shape({ label: PropTypes.string, help: PropTypes.string }).isRequired,
};

/**
 * Adjacent columns with the same `group` (A.6) as one segment; ungrouped
 * columns are a segment of their own. The order is the manifest's.
 */
export function headerSegments(columns) {
	const segments = [];
	for (const col of columns) {
		const group = col.group ?? null;
		const last = segments[segments.length - 1];
		if (group && last && last.group === group) last.columns.push(col);
		else segments.push({ group, columns: [col] });
	}
	return segments;
}

/** The labels of the columns the API's `q` searches: the text and url ones (A.6). */
export const searchedLabels = (columns) =>
	columns.filter((c) => ["text", "url"].includes(c.type)).map((c) => c.label);

/**
 * The search box above a `searchable` table (A.6): the query is sent after
 * SEARCH_DEBOUNCE_MS of quiet, so typing does not ask the API per keystroke.
 */
function SearchBox({ id, value, onChange, columns }) {
	const [text, setText] = useState(value ?? "");
	const latest = useRef(value ?? "");
	useEffect(() => {
		if (text === latest.current) return undefined;
		const t = setTimeout(() => {
			latest.current = text;
			onChange(text);
		}, SEARCH_DEBOUNCE_MS);
		return () => clearTimeout(t);
	}, [text, onChange]);
	const what = searchedLabels(columns);
	return (
		<div className="as-panel-table__search">
			<Basic
				name={`${id}-search`}
				label="Search"
				required={false}
				disabled={false}
				search
				placeholder={what.length ? `Search by ${what.join(" or ").toLowerCase()}` : "Search"}
				value={text}
				onInput={(e) => setText(e.target.value)}
				autoComplete="off"
			/>
		</div>
	);
}

SearchBox.propTypes = {
	id: PropTypes.string.isRequired,
	value: PropTypes.string,
	onChange: PropTypes.func.isRequired,
	columns: PropTypes.array.isRequired,
};

/**
 * A `table` dataset (ASB-4377) on the themed DataTable: one column per
 * manifest column, formatted by type (counts exact and right-aligned, days
 * long, URLs as monospace text with a copy button and no link, text clamped
 * to two lines with expand, enums with their role's colour and label), sorted
 * as the manifest says. When the dataset declares a `drill` and the caller
 * passes `onDrill`, a row with a value for every drill key is clickable and
 * carries an "Open" control, and the row is handed back for the caller to
 * filter the target dataset by the drill's keys; a row whose drill key is
 * null or missing has no control and no pointer cursor, and a click on it
 * does nothing (A.7: a null drill key doesn't drill). `hideColumns` drops
 * columns by key: in a drill modal the keys
 * the table was filtered by, which the breadcrumb already shows. `compact`
 * defaults to on past COMPACT_FROM columns.
 *
 * From the card review (A.6): a column's `help` shows when its header is
 * hovered or focused (one shared tooltip for the table, headers and cells);
 * adjacent columns sharing a `group` get one header cell above them; a ratio
 * under its `minBase` is greyed with the note in its tooltip; a null in a
 * column with `nullHelp` is the em dash with that tooltip. A `searchable`
 * dataset is server-side: the caller passes `server` (the query, the total
 * and an `onChange`), the table shows a search box, sends sorts as
 * `sort`/`dir` and pages 25 rows at a time with "N–M of total".
 */
function PanelTable({
	dataset,
	rows,
	onDrill,
	drillLabel,
	truncated,
	compact,
	hideColumns,
	server,
	busy,
}) {
	const uid = useId().replace(/:/g, "");
	const tipId = `as-panel-table-help-${uid}`;
	const hidden = new Set(hideColumns ?? []);
	const allColumns = dataset.columns ?? [];
	const columns = allColumns.filter((c) => !hidden.has(c.key));
	const drill = dataset.drill && onDrill ? dataset.drill : null;
	const offset = server?.offset ?? 0;
	const keyed = useMemo(
		() => (rows ?? []).map((r, i) => ({ ...r, __key: offset + i })),
		[rows, offset]
	);
	const sort = server ? { column: server.sort, dir: server.dir } : dataset.sort ?? null;
	const total = server ? server.total ?? keyed.length : keyed.length;

	const body = (col) => (row) => {
		const value = row[col.key];
		switch (col.type) {
			case "url":
				return (
					<UrlCell
						value={value}
						column={col}
					/>
				);
			case "text":
				// A text value the manifest labels ("(not recorded)") reads as an enum
				// cell: the label, its help, the stored value in the title.
				return labelEntry(col, value) ? (
					<EnumCell
						value={value}
						column={col}
					/>
				) : (
					<TextCell
						value={value}
						column={col}
					/>
				);
			case "enum":
				return (
					<EnumCell
						value={value}
						column={col}
					/>
				);
			case "pct":
				if (col.ratio) {
					return (
						<RatioCell
							value={value}
							column={col}
							columns={allColumns}
							row={row}
						/>
					);
				}
				break;
			default:
				break;
		}
		if (isBlank(value)) return <NullCell column={col} />;
		return formatValue(col.type, value, "cell");
	};
	const classFor = (col) =>
		NUMERIC_TYPES.includes(col.type) ? "as-cell--num" : `as-cell--${col.type}`;
	// A row drills only with a value for every drill key (A.7).
	const canOpen = (row) =>
		Boolean(drill) && (drill.on ?? []).every((k) => row[k] !== null && row[k] !== undefined);
	const open = (row) => {
		if (!canOpen(row)) return;
		const { __key, ...data } = row;
		onDrill(data);
	};
	const target = drillLabel ?? drill?.to ?? "";

	const segments = headerSegments(columns);
	const grouped = segments.some((s) => s.group);
	// A column with help: its th anchors the table's tooltip and is described by the help.
	const helpAnchor = (col) =>
		col.help ? { pt: { headerCell: hoverTipAnchor({ id: tipId, content: col.help }) } } : {};
	// PrimeReact styles header cells from `headerClassName`, body cells from
	// `className`; both carry the type class so a count's header aligns right.
	const headerColumn = (col, extra = {}) => (
		<Column
			key={col.key}
			field={col.key}
			header={<ColumnHeader column={col} />}
			sortable
			className={classFor(col)}
			headerClassName={classFor(col)}
			{...helpAnchor(col)}
			{...extra}
		/>
	);
	const headerGroup = grouped ? (
		<ColumnGroup>
			<Row>
				{segments.map((seg, i) =>
					seg.group ? (
						<Column
							key={`group-${i}`}
							header={seg.group}
							colSpan={seg.columns.length}
							headerClassName="as-cell--group"
						/>
					) : (
						seg.columns.map((col) => headerColumn(col, { rowSpan: 2 }))
					)
				)}
				{drill ? (
					<Column
						key="__drill"
						header=""
						rowSpan={2}
						headerClassName="as-cell--open"
					/>
				) : null}
			</Row>
			<Row>
				{segments
					.filter((seg) => seg.group)
					.flatMap((seg) => seg.columns.map((col) => headerColumn(col)))}
			</Row>
		</ColumnGroup>
	) : null;

	const serverProps = server
		? {
				lazy: true,
				totalRecords: total,
				first: offset,
				onPage: (e) => server.onChange({ offset: e.first }),
				onSort: (e) =>
					server.onChange({
						sort: e.sortField,
						dir: e.sortOrder === 1 ? "asc" : "desc",
						offset: 0,
					}),
		  }
		: {};
	const emptyMessage =
		server && server.q ? `No rows match “${server.q}”.` : "No data for this range.";

	return (
		<HoverTipScope id={tipId}>
			<div
				className={["as-panel-table-wrap", busy ? "as-panel-table-wrap--busy" : ""]
					.join(" ")
					.trim()}
				aria-busy={busy ? "true" : undefined}
			>
				{server ? (
					<SearchBox
						id={dataset.id ?? "table"}
						value={server.q}
						onChange={(q) => server.onChange({ q, offset: 0 })}
						columns={columns}
					/>
				) : null}
				<DataTable
					value={keyed}
					dataKey="__key"
					compact={compact ?? columns.length + (drill ? 1 : 0) > COMPACT_FROM}
					className={[
						"as-panel-table",
						drill ? "as-panel-table--drill" : "",
						grouped ? "as-panel-table--grouped" : "",
					]
						.join(" ")
						.trim()}
					headerColumnGroup={headerGroup}
					sortField={sort?.column}
					sortOrder={sort?.column ? (sort.dir === "asc" ? 1 : -1) : undefined}
					paginator={total > PAGE_SIZE}
					rows={PAGE_SIZE}
					paginatorTemplate={PAGINATOR}
					currentPageReportTemplate={PAGE_REPORT}
					rowHover
					onRowClick={drill ? (e) => open(e.data) : undefined}
					rowClassName={
						drill ? (row) => (canOpen(row) ? "" : "as-panel-table__row--no-drill") : undefined
					}
					emptyMessage={emptyMessage}
					footer={
						truncated
							? `Showing the first ${formatValue(
									"int",
									total
							  )} rows the API returned. Narrow the date range to see the rest.`
							: undefined
					}
					{...serverProps}
				>
					{columns.map((col) => (
						<Column
							key={col.key}
							field={col.key}
							header={<ColumnHeader column={col} />}
							sortable
							body={body(col)}
							className={classFor(col)}
							headerClassName={classFor(col)}
							{...helpAnchor(col)}
						/>
					))}
					{drill ? (
						<Column
							key="__drill"
							header=""
							className="as-cell--open"
							headerClassName="as-cell--open"
							body={(row) =>
								canOpen(row) ? (
									<IconButton
										icon={chevronIcon}
										label={`Open ${target}`}
										size="sm"
										className="as-panel-table__open"
										onClick={(e) => {
											if (e && e.stopPropagation) e.stopPropagation();
											open(row);
										}}
									/>
								) : null
							}
						/>
					) : null}
				</DataTable>
			</div>
		</HoverTipScope>
	);
}

PanelTable.propTypes = {
	/** a manifest dataset (design A.2 / A.6) with chart "table" */
	dataset: PropTypes.shape({
		id: PropTypes.string,
		columns: PropTypes.arrayOf(
			PropTypes.shape({
				key: PropTypes.string.isRequired,
				type: PropTypes.string.isRequired,
				label: PropTypes.string.isRequired,
				help: PropTypes.string,
				group: PropTypes.string,
				minBase: PropTypes.number,
				nullHelp: PropTypes.string,
				roles: PropTypes.object,
				/** enum value → { label, help } (A.6) */
				labels: PropTypes.object,
			})
		).isRequired,
		sort: PropTypes.shape({ column: PropTypes.string, dir: PropTypes.oneOf(["asc", "desc"]) }),
		drill: PropTypes.shape({ to: PropTypes.string, on: PropTypes.arrayOf(PropTypes.string) }),
		searchable: PropTypes.bool,
	}).isRequired,
	rows: PropTypes.arrayOf(PropTypes.object).isRequired,
	/** (row) => void; rows become clickable when the dataset has a drill */
	onDrill: PropTypes.func,
	/** the target dataset's title, for the Open control's name */
	drillLabel: PropTypes.string,
	/** the API capped the rows (design A.3: 1000) */
	truncated: PropTypes.bool,
	/** smaller cell padding; defaults to true past COMPACT_FROM columns */
	compact: PropTypes.bool,
	/** column keys not to render (a drill modal's filter keys) */
	hideColumns: PropTypes.arrayOf(PropTypes.string),
	/**
	 * server-side search, sort and paging (A.6 `searchable`): the query the
	 * rows were read with, the API's `total`, and `onChange(partial)` with any
	 * of `q`, `sort`, `dir`, `offset`.
	 */
	server: PropTypes.shape({
		q: PropTypes.string,
		sort: PropTypes.string,
		dir: PropTypes.oneOf(["asc", "desc"]),
		offset: PropTypes.number,
		total: PropTypes.number,
		onChange: PropTypes.func.isRequired,
	}),
	/** a re-read is in flight; the table dims and says so */
	busy: PropTypes.bool,
};

export default PanelTable;
